import { useState, useMemo } from 'react';
import { Clock, Copy, ArrowRightLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useToolUsage } from '@/hooks/useToolUsage';

const TimestampConverter = () => {
  useToolUsage('timestamp-converter');
  const [timestamp, setTimestamp] = useState(String(Math.floor(Date.now() / 1000)));
  const [dateInput, setDateInput] = useState('');
  const [isMs, setIsMs] = useState(false);

  const parsedDate = useMemo(() => {
    const ts = parseInt(timestamp);
    if (isNaN(ts)) return null;
    const ms = isMs ? ts : ts * 1000;
    const d = new Date(ms);
    if (isNaN(d.getTime())) return null;
    return d;
  }, [timestamp, isMs]);

  const dateToTimestamp = useMemo(() => {
    if (!dateInput) return '';
    const d = new Date(dateInput);
    if (isNaN(d.getTime())) return 'Invalid date';
    return String(Math.floor(d.getTime() / 1000));
  }, [dateInput]);

  const copy = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied!');
  };

  const setNow = () => setTimestamp(String(Math.floor(Date.now() / 1000)));

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center gap-3">
        <div className="p-3 rounded-xl bg-primary/20">
          <Clock className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">Timestamp Converter</h1>
          <p className="text-muted-foreground">Convert between Unix timestamps and human-readable dates</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Timestamp → Date */}
        <Card>
          <CardContent className="pt-6 space-y-4">
            <Label className="font-semibold">Unix Timestamp → Date</Label>
            <div className="flex gap-2">
              <Input value={timestamp} onChange={e => setTimestamp(e.target.value)} placeholder="e.g. 1700000000" className="font-mono" />
              <Button variant="outline" size="sm" onClick={setNow}>Now</Button>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant={!isMs ? 'default' : 'outline'} onClick={() => setIsMs(false)}>Seconds</Button>
              <Button size="sm" variant={isMs ? 'default' : 'outline'} onClick={() => setIsMs(true)}>Milliseconds</Button>
            </div>

            {parsedDate && (
              <div className="space-y-2 p-3 bg-muted/50 rounded-lg">
                {[
                  { label: 'Local', value: parsedDate.toLocaleString() },
                  { label: 'UTC', value: parsedDate.toUTCString() },
                  { label: 'ISO', value: parsedDate.toISOString() },
                  { label: 'Relative', value: getRelative(parsedDate) },
                ].map(row => (
                  <div key={row.label} className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{row.label}:</span>
                    <div className="flex items-center gap-1">
                      <span className="font-mono text-xs">{row.value}</span>
                      <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => copy(row.value)}>
                        <Copy className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Date → Timestamp */}
        <Card>
          <CardContent className="pt-6 space-y-4">
            <Label className="font-semibold">Date → Unix Timestamp</Label>
            <Input type="datetime-local" value={dateInput} onChange={e => setDateInput(e.target.value)} />

            {dateToTimestamp && (
              <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
                <span className="text-sm text-muted-foreground">Timestamp:</span>
                <span className="font-mono font-semibold text-primary flex-1">{dateToTimestamp}</span>
                <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => copy(dateToTimestamp)}>
                  <Copy className="h-3 w-3" />
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

function getRelative(d: Date): string {
  const diff = Date.now() - d.getTime();
  const abs = Math.abs(diff);
  const suffix = diff > 0 ? 'ago' : 'from now';
  if (abs < 60000) return `${Math.floor(abs / 1000)}s ${suffix}`;
  if (abs < 3600000) return `${Math.floor(abs / 60000)}m ${suffix}`;
  if (abs < 86400000) return `${Math.floor(abs / 3600000)}h ${suffix}`;
  return `${Math.floor(abs / 86400000)}d ${suffix}`;
}

export default TimestampConverter;
